/**
 * 法律文档版式（用户协议 / 隐私政策**共用**）
 *
 * 依据：N 域 PRD **§5.5**（协议与隐私政策进 P0）· `DR-020`。
 *
 * ## 🔴 三条判断
 *
 * | # | 判断 | 为什么 |
 * |:--:|---|---|
 * | 1 | **未登录也能看** | 注册前就必须能读到协议（合规要求）⇒ 路由挂在 `Shell` **之外**，本组件**不读 session** |
 * | 2 | **只用受控的极简富文本**（`**加粗**`） | 引一个 markdown 库只为两种语法不值得；而让文案作者写 JSX 会把内容和结构绑死 |
 * | 3 | **版本与生效日期页脚直出** | `LEGAL_VERSION` 单一来源；写死在页面上会在改版时撒谎 |
 *
 * ## ⚠️ 本组件不承担"合规"责任
 *
 * 它只保证**可读、可打印、结构清楚**；文本本身的可验证性见 `features/legal-content.ts` 的兑现点注释。
 */

import { Box, Button, Container, Divider, Stack, Typography } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LEGAL_EFFECTIVE_DATE, LEGAL_VERSION, type LegalSection } from '../features/legal-content.js';

/** 把 `**加粗**` 切成 React 节点（文案里只允许这一种标记） */
function inline(text: string, keyBase: string): ReactNode[] {
  const parts = text.split('**');
  return parts.map((seg, i) =>
    // 奇数段 = 加粗（以 `**` 分隔后必然交替）
    i % 2 === 1 ? (
      <Box component="strong" key={`${keyBase}-${String(i)}`} sx={{ fontWeight: 600 }}>
        {seg}
      </Box>
    ) : (
      <span key={`${keyBase}-${String(i)}`}>{seg}</span>
    ),
  );
}

export interface LegalDocProps {
  title: string;
  sections: readonly LegalSection[];
  /** 另一份文档的链接（协议 ↔ 隐私） */
  sibling: { label: string; to: string };
  testId: string;
}

export function LegalDoc({ title, sections, sibling, testId }: LegalDocProps): ReactElement {
  return (
    <Container maxWidth="md" sx={{ py: 4 }} data-testid={testId}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" data-testid={`${testId}-meta`}>
          版本 {LEGAL_VERSION} · 生效日期 {LEGAL_EFFECTIVE_DATE}
        </Typography>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2.5}>
        {sections.map((s) => (
          <Box key={s.heading} component="section">
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {s.heading}
            </Typography>
            <Stack spacing={0.75}>
              {s.paragraphs.map((p, i) => (
                <Typography
                  key={`${s.heading}-${String(i)}`}
                  variant="body2"
                  color="text.secondary"
                  component="p"
                  sx={{ m: 0, lineHeight: 1.75 }}
                >
                  {inline(p, `${s.heading}-${String(i)}`)}
                </Typography>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" spacing={2} data-testid={`${testId}-footer`}>
        <Button size="small" component={RouterLink} to={sibling.to} data-testid={`${testId}-sibling`}>
          {sibling.label}
        </Button>
        <Button size="small" component={RouterLink} to="/" data-testid={`${testId}-home`}>
          返回
        </Button>
      </Stack>
    </Container>
  );
}
